import React, { useState, useEffect, ReactNode } from 'react'
import { useCombobox } from 'downshift'
import Theme from 'core/themes/model'
import { makeStyles } from '@material-ui/styles'
import { dropdownDefaultWidth } from '../dropdown/constants'
import Text from 'core/elements/Text'
import clsx from 'clsx'
import { AlignHorizontal, AlignVertical } from 'core/components/codeMirror/CodeMirror'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import HintTooltip from 'core/elements/tooltip/HintTooltip'

interface Align {
  vertical: AlignVertical
  horizontal: AlignHorizontal
}

interface Offset {
  vertical: number
  horizontal: number
}

interface Props {
  id?: string
  label?: string
  items: string[]
  value?: string
  onChange?: (value: string) => void
  onCreate?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  allowCreate?: boolean
  errorMessage?: string
  info?: string | ReactNode
  align?: Align
  compact?: boolean
  offset?: Offset
  width?: number
}

// Example usage
// <Combobox items={['a', 'ab', 'abc', 'abcd']} label="Something" />

export default function Combobox({
  id,
  label,
  items,
  value,
  onChange,
  onCreate,
  placeholder,
  disabled = false,
  info = '',
  align,
  offset,
  width = dropdownDefaultWidth,
  compact = false,
  allowCreate = false,
  errorMessage,
}: Props) {
  const classes = useStyles({ disabled, error: errorMessage })
  const [inputItems, setInputItems] = useState(items)
  const [inputValue, setInputValue] = useState(value || '')
  const [showCreateOption, setShowCreateOption] = useState(false)

  // Sync inputItems when items prop changes (e.g., when service changes)
  useEffect(() => {
    setInputItems(items)
  }, [items])

  // Sync inputValue when value prop changes
  useEffect(() => {
    setInputValue(value || '')
  }, [value])

  const handleInputChange = (changes: any) => {
    const newInputValue = changes?.inputValue || ''
    setInputValue(newInputValue)
    // Use includes instead of startsWith for better filtering
    const filtered = items.filter((item) =>
      item.toLowerCase().includes(newInputValue.toLowerCase()),
    )
    setInputItems(filtered)
    setShowCreateOption(allowCreate && newInputValue.length > 0 && filtered.length === 0)
  }

  const handleSelectItem = (selectedItem: string) => {
    setInputValue(selectedItem)
    onChange?.(selectedItem)
  }

  const handleCreateItem = () => {
    if (inputValue && allowCreate) {
      onCreate?.(inputValue)
      onChange?.(inputValue)
      setShowCreateOption(false)
      closeMenu()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && showCreateOption) {
      event.preventDefault()
      handleCreateItem()
    }
  }
  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    selectItem,
    closeMenu,
  } = useCombobox({
    items: inputItems,
    inputValue: inputValue,
    onInputValueChange: handleInputChange,
    itemToString: (item) => item,
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        handleSelectItem(selectedItem)
      }
    },
    // Allow free-solo editing - don't reset input on blur
    onIsOpenChange: ({ isOpen: newIsOpen }) => {
      // When dropdown closes, treat current input as the value
      if (!newIsOpen && inputValue) {
        onChange?.(inputValue)
      }
    },
  })
  return (
    <div className={classes.comboboxWrapper} {...getComboboxProps()}>
      {label && (
        <Text
          {...getLabelProps()}
          component="label"
          variant="inputLabel"
          className={clsx(classes.label, 'label')}
        >
          {label}
        </Text>
      )}
      {info && (
        <HintTooltip message={info} align={align} offset={offset} className={classes.info} />
      )}
      <div
        className={clsx(classes.inputFrame, {
          [classes.expandedInputFrame]: isOpen,
          [classes.errorFrame]: !!errorMessage,
        })}
      >
        <input
          className={classes.input}
          data-testid={id ? `input--${id}` : undefined}
          {...getInputProps({
            placeholder,
            disabled,
            onKeyDown: handleKeyDown,
            onFocus: () => {
              // Open dropdown when input is focused
              if (!isOpen && inputItems.length > 0) {
                const toggleProps = getToggleButtonProps()
                if (toggleProps.onClick) {
                  toggleProps.onClick({
                    type: 'click',
                    target: null,
                    currentTarget: null,
                  } as React.MouseEvent<HTMLButtonElement>)
                }
              }
            },
          })}
        />
        <button
          type="button"
          className={classes.toggleButton}
          disabled={disabled}
          {...getToggleButtonProps()}
        >
          <FontAwesomeIcon solid size="sm">
            {isOpen ? 'chevron-up' : 'chevron-down'}
          </FontAwesomeIcon>
        </button>
        {isOpen && (
          <ul className={classes.dropdown} {...getMenuProps()}>
            {inputItems?.map((item, index) => (
              <li key={item} className={classes.dropdownItem} {...getItemProps({ item, index })}>
                <Text variant="body2">{item}</Text>
              </li>
            ))}
            {showCreateOption && (
              <li
                className={clsx(classes.dropdownItem, classes.createOption)}
                onClick={(e) => {
                  e.stopPropagation()
                  handleCreateItem()
                }}
                onMouseDown={(e) => {
                  e.preventDefault()
                }}
              >
                <FontAwesomeIcon solid size="sm" className={classes.createIcon}>
                  plus
                </FontAwesomeIcon>
                <Text variant="body2">Create "{inputValue}"</Text>
              </li>
            )}
            {inputItems.length === 0 && !showCreateOption && (
              <li className={classes.noResults}>No results found</li>
            )}
          </ul>
        )}
      </div>
      {errorMessage && (
        <Text variant="body2" className={classes.error}>
          {errorMessage}
        </Text>
      )}
    </div>
  )
}

interface DropdownStylesProps {
  width?: number
  compact?: boolean
  disabled?: boolean
  error?: string
}

const useStyles = makeStyles<Theme, DropdownStylesProps>((theme) => ({
  comboboxWrapper: {
    position: 'relative',
    margin: 0,
    maxWidth: ({ width = dropdownDefaultWidth }) => width,
    width: ({ compact }) => (compact ? 'unset' : '100%'),
    display: ({ compact }) => (compact ? 'flex' : 'block'),
    flexFlow: ({ compact }) => (compact ? 'row nowrap' : 'unset'),
    alignItems: ({ compact }) => (compact ? 'center' : 'unset'),
    justifyContent: ({ compact }) => (compact ? 'flex-end' : 'flex-end'),
    gap: 8,
  },
  inputFrame: {
    position: 'relative',
    marginTop: 1,
    borderWidth: 1,
    borderStyle: 'solid',
    cursor: ({ disabled }) => (disabled ? 'not-allowed' : 'pointer'),
    pointerEvents: ({ disabled }) => (disabled ? 'none' : 'unset'),
    borderColor: ({ error }) =>
      error ? theme.components.input.error : theme.components.input.frame.border,
    backgroundColor: ({ disabled }) =>
      disabled
        ? theme.components.input.frame.disabledBackground
        : theme.components.input.frame.background,
    transition: 'border-color .2s ease',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    '&:focus-within': {
      borderColor: theme.components.input.frame.activeBorder,
    },
  },
  input: {
    ...theme.typography.inputPlaceholder,
    flex: 1,
    minHeight: 36,
    outline: 0,
    padding: '0 8px',
    margin: 0,
    border: 'none',
    background: 'transparent',
    color: ({ disabled }) =>
      disabled
        ? theme.components.input.frame.disabledPlaceholder
        : theme.components.input.frame.color,
    '&::placeholder': {
      color: theme.components.input.frame.placeholder,
    },
    '&:disabled': {
      cursor: 'not-allowed',
    },
  },
  label: {
    display: 'inline-block',
    marginLeft: 4,
    marginRight: 4,
    gridArea: 'input-label',
    transition: 'color .2s ease',
    fontWeight: ({ compact }) => (compact ? 400 : 600),
    marginBottom: ({ compact }) => (compact ? 0 : 8),
    '&&': {
      color: theme.components.input.label.color,
    },
  },
  toggleButton: {
    padding: '0 12px',
    border: 'none',
    background: 'transparent',
    cursor: ({ disabled }) => (disabled ? 'not-allowed' : 'pointer'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
    color: theme.components.input.frame.color,
    '&:hover': {
      background: theme.palette.grey[100],
    },
    '&:disabled': {
      color: theme.components.input.frame.disabledPlaceholder,
    },
  },
  dropdown: {
    position: 'absolute',
    top: '101%',
    left: -1,
    right: -1,
    minWidth: 'max-content',
    maxWidth: 600,
    maxHeight: 350,
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: theme.components.card.background,
    border: `1px solid ${theme.components.input.frame.activeBorder}`,
    borderTop: 'none',
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    margin: 0,
    padding: '4px 0',
    listStyle: 'none',
    zIndex: 1000,
    boxShadow: theme.shadows[2],
  },
  dropdownItem: {
    padding: '8px 16px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  },
  createOption: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 500,
    color: theme.palette.primary.main,
  },
  createIcon: {
    fontSize: 12,
  },
  noResults: {
    padding: '8px 16px',
    fontSize: 14,
    color: theme.palette.grey[500],
    fontStyle: 'italic',
  },
  expandedInputFrame: {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
  },
  errorFrame: {
    borderColor: theme.components.input.error,
  },
  error: {
    color: theme.components.input.error,
    marginTop: 4,
  },
  info: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  hint: {
    cursor: 'help',
    transition: 'color .2s ease',
    color: theme.components.input.label.hint,
    '& i': {
      cursor: 'help',
      marginRight: 8,
      transition: 'color .2s ease',
      color: theme.components.input.label.hint,
      '&:hover': { color: theme.palette.grey[700] },
    },
  },
}))
